import * as React from "react";

import SelectableChip from "@v1/components/designSystem/chip/SelectableChip";
import DocumentationInfoIcon from "@v1/components/editor/page/element-editor/components/DocumentationInfoIcon";
import ModifierGroup from "@v1/components/editor/page/element-editor/components/extras/ModifierGroup";
import { useGlobalEditorActions } from "@v1/hooks/useGlobalEditorActions";
import { useVisibleBreakpoints } from "@v1/hooks/useVisibleBreakpoints";
import { canvasToStyleMap } from "@v1/utils/editor";
import { useReploHotkeys } from "@v2/hooks/useHotkeys";
import { Plus } from "lucide-react";
import { isMixedStyleValue } from "replo-runtime/store/utils/mixed-values";

const options = [
  {
    value: canvasToStyleMap.mobile,
    label: "Mobile",
    tooltipText: "Toggle mobile visibility",
  },
  {
    value: canvasToStyleMap.tablet,
    label: "Tablet",
    tooltipText: "Toggle tablet visibility",
  },
  {
    value: canvasToStyleMap.desktop,
    label: "Desktop",
    tooltipText: "Toggle desktop visibility",
  },
];

export const VisibilityModifier = () => {
  useVisibilityHotkeys();
  const { visibleBreakpoints, onChangeVisibility } = useVisibleBreakpoints();
  const isMixedVisibilityValue = isMixedStyleValue(visibleBreakpoints);

  return (
    <ModifierGroup
      title="Visibility"
      titleEnhancer={<DocumentationInfoIcon documentationType="visibility" />}
      icon={isMixedVisibilityValue ? Plus : undefined}
      iconTooltip={isMixedVisibilityValue ? "Override visibility" : undefined}
      iconShouldOpenModifierGroup
      onClick={
        isMixedVisibilityValue
          ? () => {
              onChangeVisibility([
                canvasToStyleMap.mobile,
                canvasToStyleMap.tablet,
                canvasToStyleMap.desktop,
              ]);
            }
          : undefined
      }
    >
      {isMixedVisibilityValue ? (
        <div className="text-muted text-left text-xs">
          Click the + button to override visibility for all selected components.
        </div>
      ) : (
        <div className="flex w-full flex-row gap-1 rounded-md">
          {options.map((option) => (
            <VisibilityOption
              key={option.value}
              {...option}
              isSelected={visibleBreakpoints?.includes(option.value) ?? false}
            />
          ))}
        </div>
      )}
    </ModifierGroup>
  );
};

const VisibilityOption: React.FC<{
  label: string;
  value: string;
  tooltipText: string;
  isSelected: boolean;
}> = ({ label, value, tooltipText, isSelected }) => {
  const { onChangeVisibility } = useVisibleBreakpoints();
  return (
    <SelectableChip
      size="sm"
      tooltipText={tooltipText}
      active={isSelected}
      onChange={() => {
        onChangeVisibility([value]);
      }}
    >
      <span data-testid={`visibility-toggle-${label}`}>{label}</span>
    </SelectableChip>
  );
};

function useVisibilityHotkeys() {
  const globalActions = useGlobalEditorActions();

  useReploHotkeys({
    toggleVisibility: globalActions.toggleVisibility,
  });
}
